import { createContext, type ReactNode, useContext } from "react";
import type {
  GroupConfig,
  VariableDefinition,
} from "@babackapp/variables-registry";
import type { BlockCategory } from "@babackapp/editor-blocks-library/blocks";
import type { EmailTemplateDomain } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";

/**
 * Per-domain configuration for the templates editor core.
 *
 * The core (EditorCore, the variables picker, the templates context) is
 * generic; each dedicated wrapper supplies the domain, the variable set and the
 * variable group labels through this context:
 *  - TemplatesEditor (tracking) → tracking variables + groups, domain "tracking"
 *  - ReturnTemplatesEditor (return) → return variables + groups, domain "return"
 */
export type TemplatesEditorConfig = {
  domain: EmailTemplateDomain;
  /** The variable list exposed in the picker and used for preview values. */
  variables: VariableDefinition[];
  /** Group label/icon config keyed by variable group id. */
  groups: Record<string, GroupConfig>;
  /**
   * Emails theme of the domain: `trackingPreferences.emailsTheme` for
   * tracking, `preferences.notifications.emailsTheme` for returns.
   */
  theme: Required<EmailsThemeDto>;
  /**
   * Block categories to hide from the block picker for this domain.
   * E.g. the return editor hides `BlockCategory.TRACKING` so tracking-only
   * blocks stay exclusive to the tracking editor.
   */
  hiddenBlockCategories?: BlockCategory[];
  /**
   * Whether to show the "linked workflows / not linked" banner above the
   * template name. Defaults to shown; the return editor sets it to false since
   * returns have no workflow concept yet.
   */
  showWorkflowDependencies?: boolean;
  /**
   * Templates/workflows v2 (`shop.trackingPreferences.useTemplatesAndWorkflowsV2`):
   * the template only carries the design, while the subject is defined per
   * workflow. When true, the editor hides the template-level subject field and
   * skips the "missing subject" validation. Defaults to undefined (v1 / legacy),
   * so the return editor and v1 tracking shops keep the subject on the template.
   */
  subjectManagedByWorkflow?: boolean;
  /** Max-width of the email canvas in the editor (px). Defaults to 480. */
  contentMaxWidth?: number;
};

const TemplatesEditorConfigContext = createContext<
  TemplatesEditorConfig | undefined
>(undefined);

export function TemplatesEditorConfigProvider({
  config,
  children,
}: {
  config: TemplatesEditorConfig;
  children: ReactNode;
}) {
  return (
    <TemplatesEditorConfigContext.Provider value={config}>
      {children}
    </TemplatesEditorConfigContext.Provider>
  );
}

export function useTemplatesEditorConfig(): TemplatesEditorConfig {
  const context = useContext(TemplatesEditorConfigContext);

  if (context === undefined) {
    throw new Error(
      "useTemplatesEditorConfig must be used within a TemplatesEditorConfigProvider",
    );
  }
  return context;
}
